// src/pages/legal/PrivacyPolicy.jsx
import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Helmet } from "react-helmet";

/**
 * PrivacyPolicy
 * - Clean React-Bootstrap layout
 * - Optional <Helmet> for SEO title
 * - Table of contents with in-page anchors
 * - Semantic headings and spacing utilities
 */
export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy • MyGolfDNA</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <Container className="py-4">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <header className="mb-4">
              <h1 className="h2 mb-2">Privacy Policy</h1>
              <p className="text-muted mb-0">
                Effective May 5, 2023
              </p>
            </header>

            {/* Table of Contents */}
            <nav className="mb-4">
              <h2 className="h6 text-uppercase text-secondary mb-3">On this page</h2>
              <ul className="list-unstyled d-grid gap-2">
                <li><a href="#scope" className="text-decoration-none">Scope</a></li>
                <li><a href="#collection" className="text-decoration-none">Collection of Personal Information</a></li>
                <li><a href="#use" className="text-decoration-none">Use of Personal Information</a></li>
                <li><a href="#sharing" className="text-decoration-none">Sharing with Third Parties</a></li>
                <li><a href="#tracking" className="text-decoration-none">Tracking User Behavior</a></li>
                <li><a href="#auto" className="text-decoration-none">Automatically Collected Information</a></li>
                <li><a href="#cookies" className="text-decoration-none">Cookies</a></li>
                <li><a href="#security" className="text-decoration-none">Security</a></li>
                <li><a href="#deletion" className="text-decoration-none">Right to Deletion</a></li>
                <li><a href="#children" className="text-decoration-none">Children Under 13</a></li>
                <li><a href="#email" className="text-decoration-none">E‑mail Communications</a></li>
                <li><a href="#changes" className="text-decoration-none">Changes to this Policy</a></li>
                <li><a href="#contact" className="text-decoration-none">Contact</a></li>
              </ul>
            </nav>

            <section id="scope" className="mb-4">
              <h2 className="h4">Scope</h2>
              <p>
                Protecting your private information is our priority. This Privacy Policy applies to
                mygolfdna.com and MyGolfDNA LLC (“MyGolfDNA”, “we”, “us”, or “our”) and governs
                data collection and usage for the MyGolfDNA golf instruction website (the “Site”).
                By using the Site, you consent to the data practices described in this statement.
              </p>
            </section>

            <section id="collection" className="mb-4">
              <h2 className="h4">Collection of Personal Information</h2>
              <p>
                To better provide you with products and services, MyGolfDNA may collect personally
                identifiable information such as:
              </p>
              <ul>
                <li>First and last name</li>
                <li>Mailing address</li>
                <li>E‑mail address</li>
              </ul>
              <p>
                If you purchase products or services, we collect billing and payment information to
                complete your transaction. We may also collect anonymous demographic information,
                such as your age.
              </p>
              <p>
                We do not collect personal information unless you voluntarily provide it. You may be
                required to provide certain information when you: (a) register for an account; (b) enter
                a sweepstakes or contest; (c) sign up for special offers; (d) contact us; or (e) submit
                payment information. We use this information to deliver requested services and may
                gather additional personal or non‑personal information in the future.
              </p>
            </section>

            <section id="use" className="mb-4">
              <h2 className="h4">Use of Personal Information</h2>
              <p>
                MyGolfDNA uses your information to operate and deliver the services you request and
                to inform you of other products or services available from MyGolfDNA and its affiliates.
              </p>
            </section>

            <section id="sharing" className="mb-4">
              <h2 className="h4">Sharing Information with Third Parties</h2>
              <p>We do not sell, rent, or lease customer lists to third parties.</p>
              <p>
                We may share data with trusted partners to help with statistical analysis, email or postal
                mail, customer support, or deliveries. These partners may not use your information
                except to provide services to MyGolfDNA and must maintain its confidentiality.
              </p>
              <p>
                We may disclose information without notice if required by law or in good faith to
                (a) comply with legal process; (b) protect our rights or property; or (c) protect the
                personal safety of users or the public.
              </p>
            </section>

            <section id="tracking" className="mb-4">
              <h2 className="h4">Tracking User Behavior</h2>
              <p>
                We may track pages users visit within MyGolfDNA to determine which services are most
                popular and to deliver customized content and advertising.
              </p>
            </section>

            <section id="auto" className="mb-4">
              <h2 className="h4">Automatically Collected Information</h2>
              <p>
                Information about your device may be collected automatically, such as IP address,
                browser type, domain names, access times, and referring websites. This is used for
                operation, quality maintenance, and general statistics about use of the Site.
              </p>
            </section>

            <section id="cookies" className="mb-4">
              <h2 className="h4">Use of Cookies</h2>
              <p>
                The Site may use cookies to personalize your experience and save time. Cookies are
                uniquely assigned to you and can only be read by a web server in the domain that
                issued the cookie. You can accept or decline cookies in your browser. Declining cookies
                may limit certain features.
              </p>
            </section>

            <section id="security" className="mb-4">
              <h2 className="h4">Security of Your Personal Information</h2>
              <ul>
                <li>SSL/TLS encryption in transit</li>
              </ul>
              <p>
                While we strive to protect your information, no internet or wireless transmission is
                100% secure. You acknowledge the inherent limitations of online security.
              </p>
            </section>

            <section id="deletion" className="mb-4">
              <h2 className="h4">Right to Deletion</h2>
              <p>
                Subject to certain exceptions, upon receipt of a verifiable request we will:
              </p>
              <ul>
                <li>Delete your personal information from our records; and</li>
                <li>Direct our service providers to delete your personal information from their records.</li>
              </ul>
              <p>
                We may decline a deletion request when necessary to (non‑exhaustive list): complete
                a transaction, detect/guard against security incidents, debug errors, exercise free
                speech, comply with laws (including the California Electronic Communications Privacy
                Act), enable internal uses aligned with your expectations, or otherwise use the
                information lawfully in context.
              </p>
            </section>

            <section id="children" className="mb-4">
              <h2 className="h4">Children Under Thirteen</h2>
              <p>
                We do not knowingly collect information from children under 13. If you are under 13, you
                must obtain permission from your parent or guardian to use the Site.
              </p>
            </section>

            <section id="email" className="mb-4">
              <h2 className="h4">E‑mail Communications</h2>
              <p>
                We may contact you for announcements, promotions, alerts, confirmations, and surveys.
                To improve services, we may receive a notification when you open an email or click a link.
              </p>
              <p>
                You may opt out of marketing emails by clicking “Unsubscribe” in those messages.
              </p>
            </section>

            <section id="changes" className="mb-4">
              <h2 className="h4">Changes to this Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material
                changes by email (to the address in your account), by a prominent notice on the Site,
                and/or by updating this page. Your continued use after changes constitutes acceptance.
              </p>
            </section>

            <section id="contact" className="mb-4">
              <h2 className="h4">Contact</h2>
              <address className="mb-0">
                <div className="fw-semibold">MyGolfDNA LLC</div>
                <div>Email: <a href="mailto:support@mygolfdna.com">support@mygolfdna.com</a></div>
              </address>
            </section>

            <footer className="pt-3 mt-4 border-top text-muted">
              <small>&copy; {new Date().getFullYear()} MyGolfDNA LLC. This page is provided for informational purposes and is not legal advice.</small>
            </footer>
          </Col>
        </Row>
      </Container>
    </>
  );
}
